import type {FC} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'
import {Box, Collapse, Link, Paper, Stack, SvgIcon, Typography, useTheme} from '@mui/material'
import TerrainIcon from '@mui/icons-material/Terrain'
import {
  livePulseSx,
  liveStatusSx,
} from '@containers/Dashboards/AgentChat/components/messages/liveStatusSx'
import {useOpenConversation} from '@containers/Dashboards/AgentChat/hooks/useOpenConversation'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {ToolConversationRef, ToolSummaryContent} from '@interfaces/alpinist/message'

// Tool name the backend uses when the agent loads a skill (object_name carries
// the skill's display name).
const SKILL_TOOL = 'load_skill'
// look_at_screen (AB#6762): Alpinist looked at the user's page. Named in the
// user's words, live and once the turn is over — the only UI the feature has.
const LOOK_AT_SCREEN_TOOL = 'look_at_screen'
// Conversation search (AB#6775): Alpinist searched or read the user's own earlier
// conversations. Named in the user's words, with the conversations it found as
// links that open them — the only UI the feature has.
const SEARCH_CONVERSATIONS_TOOL = 'search_my_conversations'
const READ_CONVERSATION_TOOL = 'read_conversation'
const CONVERSATION_TOOL_LABELS = new Map<string, string>([
  [SEARCH_CONVERSATIONS_TOOL, 'tool_search_my_conversations'],
  [READ_CONVERSATION_TOOL, 'tool_read_conversation'],
])

// The planning text can run to thousands of characters on a long turn, which would
// push the answer off screen. Capped to this many lines with the overflow scrolling
// inside the bubble, so the bubble's height stays fixed however much arrives.
const MAX_COMMENTARY_LINES = 3
const COMMENTARY_LINE_HEIGHT = 1.5

// Treated as "still at the bottom" when within this many pixels of it, which absorbs
// subpixel rounding so follow-along doesn't switch itself off on its own scroll.
const FOLLOW_SLACK_PX = 12

interface ToolBubbleProps {
  content: ToolSummaryContent
  final: boolean
  tone?: 'default' | 'incident'
}

const ToolBubble: FC<ToolBubbleProps> = ({content, final, tone = 'default'}) => {
  const theme = useTheme()
  const [t, i18n] = useCustomTranslation('alpinist')
  const openConversation = useOpenConversation()

  // ---- Live timer state (ms) ----
  const [displayMs, setDisplayMs] = useState<number>(content.total_ms)
  const baselineMsRef = useRef<number>(content.total_ms) // last server value
  const startTsRef = useRef<number>(Date.now()) // when we started interpolating from baseline

  // If server pushes a new total_ms, snap to it and restart interpolation
  useEffect(() => {
    baselineMsRef.current = content.total_ms
    startTsRef.current = Date.now()
    setDisplayMs(content.total_ms)
  }, [content.total_ms])

  // Drive interpolation while not final
  useEffect(() => {
    if (final) {
      // lock to the last server value when final
      setDisplayMs(content.total_ms)
      return
    }
    const id = window.setInterval(() => {
      const elapsed = Date.now() - startTsRef.current
      setDisplayMs(baselineMsRef.current + elapsed)
    }, 150) // lightweight, responsive
    return () => window.clearInterval(id)
  }, [final, content.total_ms])

  // Skills the agent loaded this turn (the load_skill tool carries the skill's
  // display name as its object_name) — shown as a persistent line so the user
  // can see which skills were used, the same way tool activity is surfaced.
  const skillNames = useMemo(
    () =>
      Array.from(
        new Set(
          content.tools
            .filter((tr) => tr.name === SKILL_TOOL)
            .map((tr) => (tr.object_name || '').trim())
            .filter(Boolean),
        ),
      ),
    [content.tools],
  )

  const lookedAtScreen = content.tools.some((tr) => tr.name === LOOK_AT_SCREEN_TOOL)
  // One row per conversation tool that ran, with the conversations it named
  // (deduplicated — a turn may search twice). A conversation the turn both found
  // and read gets a single "found and read" row instead of one under each tool.
  const conversationLines = useMemo(() => {
    const collect = (tool: string): Map<string, ToolConversationRef> | null => {
      const runs = content.tools.filter((tr) => tr.name === tool)
      if (runs.length === 0) return null
      const refs = new Map<string, ToolConversationRef>()
      runs.forEach((tr) =>
        (tr.conversations ?? []).forEach((ref) => {
          if (ref?.id && !refs.has(ref.id)) refs.set(ref.id, ref)
        }),
      )
      return refs
    }
    const searched = collect(SEARCH_CONVERSATIONS_TOOL)
    const read = collect(READ_CONVERSATION_TOOL)
    const both = read ? Array.from(read.values()).filter((ref) => searched?.has(ref.id)) : []
    const bothIds = new Set(both.map((ref) => ref.id))
    const rest = (refs: Map<string, ToolConversationRef>): ToolConversationRef[] =>
      Array.from(refs.values()).filter((ref) => !bothIds.has(ref.id))

    const rows: {key: string; label: string; refs: ToolConversationRef[]}[] = []
    // A tool's own row stays while it has conversations left to show, or if it
    // named none at all (no match, not found) — then the label alone says it ran.
    if (searched && (rest(searched).length > 0 || searched.size === 0)) {
      rows.push({
        key: SEARCH_CONVERSATIONS_TOOL,
        label: 'tool_search_my_conversations',
        refs: rest(searched),
      })
    }
    if (both.length > 0) {
      rows.push({key: 'found_and_read', label: 'tool_found_and_read_conversation', refs: both})
    }
    if (read && (rest(read).length > 0 || read.size === 0)) {
      rows.push({key: READ_CONVERSATION_TOOL, label: 'tool_read_conversation', refs: rest(read)})
    }
    return rows
  }, [content.tools])

  const formatDate = (iso?: string | null): string | undefined => {
    if (!iso) return undefined
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? undefined : date.toLocaleDateString(i18n.language)
  }

  // The agent's planning for this turn, oldest first. Streamed in live: the entries
  // grow token by token while the turn runs, which is the point — a long run with
  // few tool calls otherwise looks hung.
  const commentary = useMemo(() => content.commentary ?? [], [content.commentary])

  // Collapsed only once the turn is over, where it is history rather than progress.
  const [expanded, setExpanded] = useState(false)
  const showFullCommentary = !final || expanded

  // Because the list is capped, new text arrives below the fold — follow it down
  // while the turn runs, unless the user has scrolled up to read something, which
  // following would fight. Stickiness is decided when a scroll happens, NOT by
  // measuring after new text lands: one update can append more text than the lane is
  // tall, so a post-growth measurement reads every normal update as "scrolled away"
  // and follow-along switches itself off permanently.
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const stickToBottom = useRef(true)

  const handleCommentaryScroll = (): void => {
    const el = scrollRef.current
    if (!el) return
    // Our own writes land at the bottom and so keep this true; only a deliberate
    // scroll away clears it, and coming back to the bottom restores it.
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el || final || !stickToBottom.current) return
    // A frame late on purpose: the Collapse is still animating its height on the
    // first pass, so scrolling now would land against a stale scrollHeight.
    const frame = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
    return () => cancelAnimationFrame(frame)
  }, [commentary, final, showFullCommentary])

  // Markdown emphasis in raw planning text reads as noise at caption size.
  const plain = (value: string): string => value.replace(/[*_`#]/g, '').trim()

  // Format display text using live/locked ms
  const text = useMemo(() => {
    const totalSec = Math.max(0, final ? content.total_ms : displayMs) / 1000
    const sec = totalSec.toFixed(0)
    const {count} = content
    const label = count === 1 ? t('tool_singular') : t('tool_plural')
    if (content.tools.length === 0) {
      // A turn can produce planning without ever calling a tool — still show the
      // elapsed time so the bubble isn't blank.
      return commentary.length > 0 ? t('tool_thinking_line', {seconds: sec}) : null
    }
    if (final) {
      // e.g. "3 tools • 1.25s"
      return t('tool_summary_line', {count, label, seconds: sec})
    } else {
      const lastTool = content.tools[content.tools.length - 1]
      let name: string
      if (!lastTool) {
        name = t('tool_default_name')
      } else if (lastTool.name === LOOK_AT_SCREEN_TOOL) {
        name = t('tool_look_at_screen')
      } else if (CONVERSATION_TOOL_LABELS.has(lastTool.name)) {
        name = t(CONVERSATION_TOOL_LABELS.get(lastTool.name) as string)
      } else if (lastTool.name === SKILL_TOOL) {
        // e.g. "Using skill · Create a scheduled prompt"
        name = lastTool.object_name
          ? `${t('tool_load_skill')} · ${lastTool.object_name}`
          : t('tool_load_skill')
      } else {
        name = `${lastTool.name} ${lastTool.object_name}`
      }
      // Remove all the text from name that is within **..**
      const cleanName = name.replace(/\*\*.*?\*\*/g, '').trim()

      // e.g. "Vector DB • 0.8s"
      return t('tool_live_line', {count, label, name: cleanName, seconds: sec})
    }
  }, [final, content, displayMs, commentary, t])

  const showPulse = !final
  const showBorder = !final

  return (
    <Paper sx={liveStatusSx(theme, tone, showBorder)}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <SvgIcon
          component={TerrainIcon}
          fontSize="small"
          sx={showPulse ? livePulseSx : undefined}
        />
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Stack>
      {skillNames.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{display: 'block', mt: 0.5, pl: 3.5}}
        >
          {t('tool_skill_used', {count: skillNames.length, names: skillNames.join(', ')})}
        </Typography>
      )}

      {lookedAtScreen && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{display: 'block', mt: 0.5, pl: 3.5}}
          data-testid="alpinist-tool-looked-at-screen"
        >
          {t('tool_look_at_screen')}
        </Typography>
      )}
      {conversationLines.map(({key, label, refs}) => (
        <Box key={key} sx={{mt: 0.5, pl: 3.5}} data-testid={`alpinist-tool-${key}`}>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
            {t(label)}
          </Typography>
          {/* One per line: the panel is narrow and titles are long. */}
          {refs.map((ref) => (
            <Link
              key={ref.id}
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              onClick={() => openConversation(ref.id)}
              title={formatDate(ref.last_activity)}
              sx={{display: 'block', textAlign: 'left'}}
            >
              {ref.title?.trim() || t('conversation_untitled')}
            </Link>
          ))}
        </Box>
      ))}

      {commentary.length > 0 && (
        <>
          <Collapse in={showFullCommentary} unmountOnExit>
            <Stack
              ref={scrollRef}
              onScroll={handleCommentaryScroll}
              spacing={0.75}
              sx={{
                mt: 0.5,
                pl: 3.5,
                pr: 1,
                // `em` resolves against the caption size set here, so the cap is
                // exactly MAX_COMMENTARY_LINES lines of this text at any root size.
                fontSize: (th) => th.typography.caption.fontSize,
                maxHeight: `${MAX_COMMENTARY_LINES * COMMENTARY_LINE_HEIGHT}em`,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                '&::-webkit-scrollbar': {width: 6},
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 3,
                  backgroundColor: 'divider',
                },
              }}
            >
              {commentary.map((entry, index) => {
                const isLast = index === commentary.length - 1
                return (
                  <Typography
                    key={entry.t_start ?? index}
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: COMMENTARY_LINE_HEIGHT,
                      borderLeft: 2,
                      borderColor: 'divider',
                      pl: 1,
                      // The entry still streaming reads as current, the rest as done.
                      fontStyle: !final && isLast ? 'italic' : 'normal',
                      opacity: !final && !isLast ? 0.7 : 1,
                    }}
                  >
                    {plain(entry.text)}
                  </Typography>
                )
              })}
            </Stack>
          </Collapse>

          {/* Only offered once the run is over — during it, hiding progress is the
              opposite of what this is for. */}
          {final && (
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              color="text.secondary"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              sx={{display: 'block', mt: 0.5, pl: 3.5, textAlign: 'left'}}
            >
              {expanded
                ? t('tool_reasoning_hide')
                : t('tool_reasoning_show', {count: commentary.length})}
            </Link>
          )}
        </>
      )}
    </Paper>
  )
}

export default ToolBubble
